/**
 * Program analytics → xlsx. Three sheets: program summary, course-by-day
 * cohort performance (with the all-time course average for comparison),
 * and the per-learner rollup. Uses the same write-excel-file cell model
 * as exportWorkbook.ts.
 */
import type { ProgramAnalytics } from '../../services/analytics';
import type { WorkbookRow } from './exportWorkbook';

const bold = (value: string): { value: string; fontWeight: 'bold' } => ({
  value,
  fontWeight: 'bold',
});
const cell = (value: string | number | null | undefined) => ({
  value: value === null || value === undefined ? 'N/A' : value,
});

export const buildProgramSummarySheet = (d: ProgramAnalytics): WorkbookRow[] => [
  [bold('Program'), cell(d.program.name)],
  [bold('Status'), cell(d.program.status)],
  [bold('Duration (days)'), cell(d.program.duration_days)],
  [bold('Courses in program'), cell(d.kpis.courses_total)],
  [bold('Learners enrolled'), cell(d.kpis.learners_enrolled)],
  [bold('Program completion rate (%)'), cell(d.kpis.program_completion_rate)],
  [bold('Average progress (%)'), cell(d.kpis.avg_progress)],
];

export const buildProgramCoursesSheet = (d: ProgramAnalytics): WorkbookRow[] => [
  [
    bold('Day'),
    bold('Course'),
    bold('Learners completed'),
    bold('Learners total'),
    bold('Avg. quiz score (%)'),
    bold('Avg. rating (out of 5)'),
  ],
  ...d.days.flatMap((day) =>
    day.courses.map((c): WorkbookRow => [
      cell(day.day_number),
      cell(c.title),
      cell(Math.round(((c.kpis?.completion_rate ?? 0) * (c.kpis?.learners ?? 0)) / 100)),
      cell(c.kpis?.learners ?? 0),
      cell(c.kpis?.quiz_avg ?? null),
      cell(c.kpis?.rating_avg ?? null),
    ])
  ),
];

export const buildProgramLearnersSheet = (d: ProgramAnalytics): WorkbookRow[] => [
  [
    bold('Learner'),
    bold('Courses completed'),
    bold('Courses total'),
    bold('Program progress (%)'),
    bold('Average quiz score (%)'),
  ],
  ...d.learners.map((l): WorkbookRow => [
    cell(l.name || '—'),
    cell(l.courses_completed),
    cell(l.courses_total),
    cell(l.progress_percent),
    cell(l.avg_score),
  ]),
];

export const PROGRAM_SHEET_NAMES = ['Program Summary', 'Courses by Day', 'Learners'];

export const downloadProgramWorkbook = async (d: ProgramAnalytics): Promise<void> => {
  const { default: writeXlsxFile } = await import('write-excel-file/browser');
  const sheets = [
    buildProgramSummarySheet(d),
    buildProgramCoursesSheet(d),
    buildProgramLearnersSheet(d),
  ];
  const safe = d.program.name.replace(/[^a-z0-9]+/gi, '-');
  await writeXlsxFile(
    PROGRAM_SHEET_NAMES.map((name, index) => ({ name, data: sheets[index] }))
  ).toFile(`${safe}-program-analytics.xlsx`);
};
